import { buildAccountCache } from './buildAccountCache';
import { buildOrgAggregateCache } from './buildOrgAggregateCache';
import {
  enrichOrgParentEntry,
  resolveOrgParentEntries,
  shouldBuildOrgAggregate,
  type OrgParentEntry,
} from './orgVolumeUtils';
import { stampVolumeAccountNames } from './stampVolumeAccountNames';

import type { Account } from '~/shared/utils/types';
import type {
  ExplorationAccountCache,
  ExplorationVolume,
} from '../types/exploration';

/**
 * Groups already-transformed volumes into one cache per account, plus org-aggregate
 * caches for parent entries that pass `includeOrgAggregate`. Used by the live API
 * loader (`buildExplorationVolumeCaches.ts`).
 */
export function buildAccountCachesFromVolumes(
  volumes: ExplorationVolume[],
  authAccounts: Account[],
  includeOrgAggregate: (entry: OrgParentEntry) => boolean = () => true
): Record<string, ExplorationAccountCache> {
  const nameMap: Record<string, string> = {};
  authAccounts.forEach((a) => {
    nameMap[a.accountId] = a.accountName;
  });

  const stampedVolumes = stampVolumeAccountNames(volumes, nameMap);

  const caches: Record<string, ExplorationAccountCache> = {};
  const byAcct = new Map<string, ExplorationVolume[]>();
  stampedVolumes.forEach((v) => {
    if (!v.accountId) {
      return;
    }
    if (!byAcct.has(v.accountId)) {
      byAcct.set(v.accountId, []);
    }
    byAcct.get(v.accountId)!.push(v);
  });

  byAcct.forEach((vols, acctId) => {
    caches[acctId] = buildAccountCache(acctId, nameMap[acctId] || acctId, vols);
  });

  resolveOrgParentEntries(authAccounts)
    .filter(includeOrgAggregate)
    .forEach((entry) => {
      const enriched = enrichOrgParentEntry(entry, authAccounts);
      if (!shouldBuildOrgAggregate(enriched)) {
        return;
      }
      buildOrgAggregateCache(caches, enriched, nameMap);
    });

  return caches;
}
